import { useColorMode } from '@chakra-ui/react';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import {
  ThemePreference,
  ThemePreferenceContext,
} from 'src/contexts/ThemePreferenceContext';

const THEME_PREFERENCE_KEY = 'theme-preference';

/**
 * Chakra never persists 'system' - setColorMode('system') stores the
 * resolved light/dark value. The user's actual intent (including 'system')
 * lives under our own localStorage key; absence of the key means 'system'.
 */
const getStoredPreference = (): ThemePreference => {
  const stored = localStorage.getItem(THEME_PREFERENCE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
};

export const ThemePreferenceProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { setColorMode } = useColorMode();
  const [preference, setPreferenceState] =
    useState<ThemePreference>(getStoredPreference);

  const setPreference = useCallback(
    (next: ThemePreference) => {
      localStorage.setItem(THEME_PREFERENCE_KEY, next);
      setPreferenceState(next);
      setColorMode(next);
    },
    [setColorMode],
  );

  useEffect(() => {
    if (preference !== 'system') return;
    // Re-resolve on mount: corrects a stale stored color mode when the OS
    // preference changed since the last visit.
    setColorMode('system');
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => setColorMode('system');
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [preference, setColorMode]);

  const value = useMemo(
    () => ({ preference, setPreference }),
    [preference, setPreference],
  );

  return (
    <ThemePreferenceContext.Provider value={value}>
      {children}
    </ThemePreferenceContext.Provider>
  );
};
